export type ShippingProvider =
  | "royal_mail"
  | "inpost"
  | "evri"
  | "collection";

export type ShippingOption = {
  id: string;
  label: string;
  description: string;
  costPence: number;
  provider: ShippingProvider;
  providerLabel: string;
  tracked: boolean;
  signature?: boolean;
  compensationPence?: number;
  /**
   * When false, the provider is not currently offered on BidNova and
   * MUST NOT be shown to buyers or presented as a selectable option.
   * The entry is kept only so historic orders / labels resolve safely.
   */
  available?: boolean;
};

// UK shipping options offered on BidNova. costPence is the postage charged to
// the buyer at checkout — this is unchanged for existing Royal Mail services.
export const SHIPPING_OPTIONS: ShippingOption[] = [
  {
    id: "royal_mail_tracked_48",
    label: "Royal Mail Tracked 48",
    description: "Reliable tracked delivery suitable for most trading cards.",
    costPence: 385,
    provider: "royal_mail",
    providerLabel: "Royal Mail",
    tracked: true,
    compensationPence: 15000,
    available: true,
  },
  {
    id: "royal_mail_tracked_24",
    label: "Royal Mail Tracked 24",
    description: "Faster tracked delivery for urgent deliveries.",
    costPence: 445,
    provider: "royal_mail",
    providerLabel: "Royal Mail",
    tracked: true,
    compensationPence: 15000,
    available: true,
  },
  {
    id: "royal_mail_special",
    label: "Royal Mail Special Delivery Guaranteed",
    description:
      "Recommended for valuable items requiring signature and compensation.",
    costPence: 860,
    provider: "royal_mail",
    providerLabel: "Royal Mail",
    tracked: true,
    signature: true,
    compensationPence: 50000,
    available: true,
  },
  // Kept for historic-order resolution only. NOT buyer-facing.
  {
    id: "inpost_locker",
    label: "InPost Locker",
    description: "Future direct locker delivery integration.",
    costPence: 399,
    provider: "inpost",
    providerLabel: "InPost",
    tracked: true,
    available: false,
  },
  {
    id: "evri_standard",
    label: "Evri Standard",
    description: "Future direct courier integration.",
    costPence: 329,
    provider: "evri",
    providerLabel: "Evri",
    tracked: true,
    available: false,
  },
  {
    id: "collection",
    label: "Collection",
    description: "Arrange collection directly with the buyer.",
    costPence: 0,
    provider: "collection",
    providerLabel: "Collection",
    tracked: false,
    available: true,
  },
  {
    id: "international_tracked",
    label: "International Tracked",
    description: "Tracked outside the UK",
    costPence: 1350,
    provider: "royal_mail",
    providerLabel: "Royal Mail",
    tracked: true,
    available: true,
  },
];

/**
 * Shipping options that are currently available for buyers and sellers to pick.
 * Excludes providers marked `available: false` (e.g. future integrations).
 */
export const AVAILABLE_SHIPPING_OPTIONS: ShippingOption[] = SHIPPING_OPTIONS.filter(
  (o) => o.available !== false,
);

export function findShipping(id: string | null | undefined): ShippingOption | undefined {
  if (!id) return undefined;
  return SHIPPING_OPTIONS.find((o) => o.id === id);
}
